import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../utils/cn';
import { EncomendaSort, EncomendaSortKey } from '../utils/encomendaSort';

interface Props {
  label: string;
  sortKey: EncomendaSortKey;
  sort: EncomendaSort;
  onSort: (key: EncomendaSortKey) => void;
  /** Typography/colour for the host table — the page and the Operational panel differ. */
  className?: string;
  /**
   * Colour for the column currently doing the sorting. Supplied by the host because the
   * Operational panel renders on dark as well as light, where a hardcoded near-black would be
   * invisible. Applied LAST: `cn` is tailwind-merge, so the last conflicting class wins — with
   * the active colour earlier in the list the host's own text colour silently overrode it and
   * the active column looked exactly like the others.
   */
  activeClassName?: string;
}

/**
 * A clickable table header that reports its own sort state.
 *
 * `aria-sort` rather than colour alone: which column is ordering the table, and in which
 * direction, is information — a tinted chevron conveys it to sighted users only, and to nobody at
 * all in a screen reader. It is also what lets a test assert the state rather than a class name.
 */
export default function SortableColumnHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
  activeClassName = 'text-gray-900',
}: Props) {
  const active = sort.key === sortKey;
  const direction = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    // aria-sort goes on the HEADER, which is what carries the columnheader role — it is invalid
    // on the button inside it, and a screen reader reading the column would not have picked it up
    // there.
    <th scope="col" aria-sort={direction} className="text-left px-3 py-2.5 whitespace-nowrap">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'group inline-flex items-center gap-1 font-semibold uppercase tracking-wide transition-colors',
          className,
          active ? activeClassName : 'hover:opacity-80'
        )}
        title={`Ordenar por ${label}`}
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          // Shown faintly until hovered: every column being permanently decorated with arrows
          // reads as noise, but a column with no affordance at all is not discoverable.
          <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-40 transition-opacity" />
        )}
      </button>
    </th>
  );
}
